// Cave84 — Vintage Charts hooks (Phase 3 Week 11)
// TanStack Query wrappers for services/vintageCharts.ts

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { VintageChart } from '@cave84/shared-types';
import {
  enqueueVintageChartYear,
  getVintageChartByRegionYear,
  listAllVintageCharts,
  listVintageChartsByRegion,
  updateVintageChart,
  type VintageChartUpdateInput,
} from '@/services/vintageCharts';
import { qk } from '@/services/firestore';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Full matrix — all regions × all years. ~660 docs; cached aggressively. */
export function useAllVintageCharts(): UseQueryResult<VintageChart[]> {
  return useQuery({
    queryKey: qk.vintageCharts.all,
    queryFn: listAllVintageCharts,
    staleTime: 10 * 60 * 1000, // 10 min — reference data changes rarely
  });
}

/** Single region — used when navigating directly to a region view. */
export function useVintageChartsByRegion(
  region: string | undefined
): UseQueryResult<VintageChart[]> {
  return useQuery({
    queryKey: region ? qk.vintageCharts.byRegion(region) : qk.vintageCharts.byRegion('__noop__'),
    queryFn: () => (region ? listVintageChartsByRegion(region) : Promise.resolve([])),
    enabled: !!region,
    staleTime: 10 * 60 * 1000,
  });
}

/** Single (region, year) — used by WineAnalysisPage cross-reference widget. */
export function useVintageChartEntry(
  region: string | undefined,
  year: number | undefined
): UseQueryResult<VintageChart | null> {
  return useQuery({
    queryKey:
      region && year
        ? qk.vintageCharts.byRegionYear(region, year)
        : qk.vintageCharts.byRegionYear('__noop__', 0),
    queryFn: () =>
      region && year ? getVintageChartByRegionYear(region, year) : Promise.resolve(null),
    enabled: !!region && !!year,
    staleTime: 10 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Owner manual edit of a single vintage chart entry. */
export function useUpdateVintageChart(): UseMutationResult<
  void,
  Error,
  { region: string; year: number; input: Partial<VintageChartUpdateInput> }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ region, year, input }) => updateVintageChart(region, year, input),
    onSuccess: (_data, { region, year }) => {
      void qc.invalidateQueries({ queryKey: qk.vintageCharts.all });
      void qc.invalidateQueries({ queryKey: qk.vintageCharts.byRegion(region) });
      void qc.invalidateQueries({ queryKey: qk.vintageCharts.byRegionYear(region, year) });
    },
  });
}

/** Enqueue a new vintage year for CLI generation. */
export function useEnqueueVintageChartYear(): UseMutationResult<
  string,
  Error,
  { region: string; year: number }
> {
  return useMutation({
    mutationFn: ({ region, year }) => enqueueVintageChartYear(region, year),
  });
}
